import { Navigate, createBrowserRouter, useParams } from "react-router-dom";

import { AuthGuard } from "@/app/AuthGuard";
import { LandingPage } from "@/pages/LandingPage";
import { SignInPage } from "@/pages/SignInPage";
import { SignUpPage } from "@/pages/SignUpPage";
import { VerifyOtpPage } from "@/pages/VerifyOtpPage";
import { JoinEmailPage } from "@/pages/JoinEmailPage";
import { VerifyCodePage } from "@/pages/VerifyCodePage";
import { OnboardingPage } from "@/pages/OnboardingPage";
import { ContinueRegistrationPage } from "@/pages/ContinueRegistrationPage";
import { MyMeetupsPage } from "@/pages/MyMeetupsPage";
import { MyMatchesPage } from "@/pages/MyMatchesPage";
import { ParticipantProfilePage } from "@/pages/ParticipantProfilePage";
import { MyPitchPage } from "@/pages/MyPitchPage";
import { AccountSettingsPage } from "@/pages/AccountSettingsPage";
import { HowItWorksPage } from "@/pages/HowItWorksPage";
import { MyEventsPage } from "@/pages/MyEventsPage";
import { CreateEventPage } from "@/pages/CreateEventPage";
import { EventDashboardPage } from "@/pages/EventDashboardPage";
import { EventCancelPage, EventSettingsPage } from "@/pages/EventSettingsPage";
import { ParticipantDetailPage } from "@/pages/ParticipantDetailPage";
import { FinalReportPage } from "@/pages/FinalReportPage";
import { PricingPage } from "@/pages/PricingPage";
import { EntryPage } from "@/pages/EntryPage";
import { WelcomePage } from "@/pages/WelcomePage";
import { GoalsPage } from "@/pages/GoalsPage";
import { PitchPage } from "@/pages/PitchPage";
import { PitchAnalysisPage } from "@/pages/PitchAnalysisPage";
import { ConnectTelegramStepPage } from "@/pages/ConnectTelegramStepPage";
import { PitchAdvicePage } from "@/pages/PitchAdvicePage";
import { ProfilePage } from "@/pages/ProfilePage";
import { FeedPage } from "@/pages/FeedPage";
import { MatchesPage } from "@/pages/MatchesPage";
import { ProfileSettingsPage } from "@/pages/ProfileSettingsPage";
import { EventWrapPage } from "@/pages/EventWrapPage";
import { SurveyPage } from "@/pages/SurveyPage";
import { AccountHomePage } from "@/pages/AccountHomePage";
import { AuthCallbackPage } from "@/pages/AuthCallbackPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import {
  AboutPage,
  AccountPage,
  AnalyticsAggregatePage,
  FaqPage,
  PrivacyPolicyPage,
  RulesPage,
  TermsPage,
} from "@/pages/StaticPages";

function LegacyRegisterRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/e/${slug ?? ""}/join`} replace />;
}

function LegacySignUpRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/e/${slug ?? ""}/join`} replace />;
}

export const router = createBrowserRouter([
  // Public
  { path: "/", element: <LandingPage /> },
  { path: "/sign-in", element: <SignInPage /> },
  { path: "/login", element: <Navigate to="/sign-in" replace /> },
  { path: "/sign-in/verify", element: <VerifyOtpPage /> },
  { path: "/sign-up", element: <SignUpPage /> },
  { path: "/auth/callback", element: <AuthCallbackPage /> },
  { path: "/onboarding", element: <AuthGuard><OnboardingPage /></AuthGuard> },

  // Footer / static
  { path: "/faq", element: <FaqPage /> },
  { path: "/rules", element: <RulesPage /> },
  { path: "/about", element: <AboutPage /> },
  { path: "/privacy", element: <PrivacyPolicyPage /> },
  { path: "/terms", element: <TermsPage /> },

  // Organizer (auth required)
  { path: "/how-it-works", element: <AuthGuard><HowItWorksPage /></AuthGuard> },
  { path: "/events", element: <AuthGuard><MyEventsPage /></AuthGuard> },
  { path: "/events/new", element: <AuthGuard><CreateEventPage /></AuthGuard> },
  { path: "/events/analytics", element: <AuthGuard><AnalyticsAggregatePage /></AuthGuard> },
  { path: "/events/:eventId", element: <AuthGuard><EventDashboardPage /></AuthGuard> },
  { path: "/events/:eventId/settings", element: <AuthGuard><EventSettingsPage /></AuthGuard> },
  { path: "/events/:eventId/cancel", element: <AuthGuard><EventCancelPage /></AuthGuard> },
  { path: "/events/:eventId/report", element: <AuthGuard><FinalReportPage /></AuthGuard> },
  {
    path: "/events/:eventId/participants/:participantId",
    element: <AuthGuard><ParticipantDetailPage /></AuthGuard>,
  },
  { path: "/account", element: <AuthGuard><AccountPage /></AuthGuard> },
  { path: "/pricing", element: <AuthGuard><PricingPage /></AuthGuard> },

  // Authenticated participant area (Supabase Auth)
  { path: "/me/meetups", element: <AuthGuard><MyMeetupsPage /></AuthGuard> },
  { path: "/me/matches", element: <AuthGuard><MyMatchesPage /></AuthGuard> },
  { path: "/me/pitch", element: <AuthGuard><MyPitchPage /></AuthGuard> },
  { path: "/me/account", element: <AuthGuard><AccountSettingsPage /></AuthGuard> },

  // Participant flow
  { path: "/join", element: <EntryPage /> },
  { path: "/me", element: <AccountHomePage /> },
  { path: "/e/:slug", element: <WelcomePage /> },
  { path: "/e/:slug/join", element: <JoinEmailPage /> },
  { path: "/e/:slug/verify", element: <VerifyCodePage /> },
  { path: "/e/:slug/continue", element: <AuthGuard><ContinueRegistrationPage /></AuthGuard> },
  { path: "/e/:slug/connect-telegram", element: <AuthGuard><ConnectTelegramStepPage /></AuthGuard> },
  { path: "/e/:slug/sign-up", element: <LegacySignUpRedirect /> },
  { path: "/e/:slug/register", element: <LegacyRegisterRedirect /> },
  { path: "/e/:slug/goals", element: <GoalsPage /> },
  { path: "/e/:slug/pitch-advice", element: <PitchAdvicePage /> },
  { path: "/e/:slug/pitch", element: <PitchPage /> },
  { path: "/e/:slug/pitch/analysis", element: <PitchAnalysisPage /> },
  { path: "/e/:slug/profile", element: <ProfilePage /> },
  { path: "/e/:slug/feed", element: <FeedPage /> },
  { path: "/e/:slug/matches", element: <MatchesPage /> },
  { path: "/e/:slug/profile-settings", element: <ProfileSettingsPage /> },
  { path: "/e/:slug/wrap", element: <EventWrapPage /> },
  { path: "/e/:slug/survey", element: <SurveyPage /> },

  // Public participant profile — linked from the auto-generated Telegram
  // intro message after a mutual match.
  { path: "/p/:participantId", element: <ParticipantProfilePage /> },

  // 404
  { path: "*", element: <NotFoundPage /> },
]);
